<template>
  <form class="acs" novalidate @submit.prevent="submit">
    <label class="acs-label" :for="inputId">{{ label }}</label>
    <div class="acs-row">
      <input
        :id="inputId"
        v-model="areaCode"
        type="text"
        inputmode="numeric"
        maxlength="3"
        class="acs-input"
        :class="{ 'is-invalid': !!error }"
        :placeholder="placeholder"
        :disabled="disabled || searching"
        :aria-describedby="error ? inputId + '-error' : null"
        data-test="acs-area-code"
        @input="error = ''"
      >
      <button
        type="submit"
        class="acs-button"
        :disabled="disabled || searching"
        data-test="acs-search"
      >
        <b-spinner v-if="searching" small class="mr-2" />{{ searching ? 'Searching…' : 'Search' }}
      </button>
    </div>
    <p v-if="hint" class="acs-hint">{{ hint }}</p>
    <p v-if="error" :id="inputId + '-error'" class="acs-error" data-test="acs-error">{{ error }}</p>
  </form>
</template>

<script>
/**
 * "Search by area code" — shared by the A2P wizard's texting-number step and the WhatsApp wizard's
 * number step (2026-09-11). It used to exist only inside A2PWizardPhoneNumbers.vue.
 *
 * It only ASKS: `search` is emitted with a valid area code (or '' when `allowEmpty` and the box is
 * blank — the A2P step's "leave it blank" search). The parent owns the request, because the two
 * screens search through different endpoints. A typo is caught here with the server's own rule
 * (phoneNumbers.js normalizeAreaCode mirrors it) instead of costing a round trip.
 */
import { normalizeAreaCode, AREA_CODE_HINT_ERROR } from './phoneNumbers';

let inputSeq = 0;

export default {
  name: 'AreaCodeSearch',

  props: {
    label: { type: String, default: 'Search by area code' },
    hint: { type: String, default: '' },
    placeholder: { type: String, default: 'e.g. 561' },
    searching: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    /** Allow searching with the box blank (emits ''). Off = an area code is required. */
    allowEmpty: { type: Boolean, default: false },
  },

  data() {
    inputSeq += 1;
    return { areaCode: '', error: '', inputId: `acs-input-${inputSeq}` };
  },

  methods: {
    submit() {
      if (this.disabled || this.searching) return;
      const typed = String(this.areaCode || '').trim();
      if (typed === '' && this.allowEmpty) {
        this.$emit('search', '');
        return;
      }
      const code = normalizeAreaCode(typed);
      if (!code) {
        this.error = AREA_CODE_HINT_ERROR;
        return;
      }
      this.$emit('search', code);
    },
  },
};
</script>

<style scoped>
.acs {
  max-width: 420px;
  margin: 0;
}

.acs-label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.85rem;
  font-weight: 700;
  color: #262b2a;
}

.acs-row {
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
}

.acs-input {
  flex: 0 0 7rem;
  min-width: 0;
  padding: 0.45rem 0.7rem;
  font-size: 0.95rem;
  letter-spacing: 0.08em;
  color: #0c0f0e;
  background: #fff;
  border: 1px solid #c8cfcd;
  border-radius: 10px;
}

.acs-input:focus {
  outline: none;
  border-color: #0f7259;
  box-shadow: 0 0 0 3px rgba(15, 114, 89, 0.12);
}

.acs-input.is-invalid {
  border-color: #e5484d;
}

.acs-button {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 1rem;
  font-size: 0.88rem;
  font-weight: 700;
  color: #0f7259;
  background: #fff;
  border: 1px solid #c8cfcd;
  border-radius: 10px;
  cursor: pointer;
}

.acs-button:hover:not(:disabled) {
  border-color: #0f7259;
  background: #e9f6f1;
}

.acs-button:disabled {
  cursor: default;
  opacity: 0.55;
}

.acs-hint {
  margin: 0.35rem 0 0;
  font-size: 0.78rem;
  color: #9ca4a2;
}

.acs-error {
  margin: 0.35rem 0 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: #a5292e;
}
</style>
